/**
 * Central upload limits for the Image Asset Center bulk / folder upload
 * (VistoeraTASK-00522). EVERY number used by the scan, the manager and the UI
 * comes from `resolveUploadLimits` - never a literal at a call site.
 *
 * The server owns the real values (`/uploads/config`: `max_batch_files`,
 * `max_batch_total_mb`, `client_concurrency`, `max_size_mb`,
 * `max_concurrency`, `allowed_mime`); the constants below are only the
 * documented fallbacks for an older backend that does not send them yet.
 */

export const MB = 1024 * 1024

/** Product guarantee: a normal single image of at least this size must never
 * be rejected by the frontend or by the signing / metadata path. */
export const MIN_SUPPORTED_SINGLE_FILE_BYTES = 30 * MB

export const DEFAULT_UPLOAD_LIMITS = {
  maxFiles: 500,
  maxTotalBytes: 2048 * MB,
  /** Server default `upload_max_size_mb` (200). */
  maxFileBytes: 200 * MB,
  concurrency: 3,
  minConcurrency: 2,
  maxConcurrency: 5,
  /** Total tries per file, including the first. */
  retryMaxAttempts: 3,
  retryBaseDelayMs: 1000,
  retryMaxDelayMs: 15000,
  /** Trailing throttle for refreshing the visible list while a batch runs. */
  listRefreshIntervalMs: 8000,
  allowedMime: ['image/jpeg', 'image/png', 'image/webp'],
} as const

/** Extension -> MIME for browsers that give a folder file an empty/generic
 * `File.type`. Only the formats the existing upload system already supports
 * (kept in step with `allowedMime`; never widens the supported set). */
export const EXTENSION_TO_MIME: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
}

/** Image formats we recognise but the product does not accept (reported as
 * "unsupported image format", distinct from "not an image"). */
export const KNOWN_UNSUPPORTED_IMAGE_EXTENSIONS: ReadonlySet<string> = new Set([
  'gif', 'bmp', 'tif', 'tiff', 'heic', 'heif', 'avif', 'svg', 'ico', 'psd', 'raw', 'cr2', 'nef', 'arw', 'dng',
])

export interface UploadConfigLike {
  max_size_mb?: number
  allowed_mime?: string[]
  max_concurrency?: number
  max_batch_files?: number
  max_batch_total_mb?: number
  client_concurrency?: number
  direct_to_r2?: boolean
}

export interface UploadLimits {
  maxFiles: number
  maxTotalBytes: number
  maxFileBytes: number
  concurrency: number
  retryMaxAttempts: number
  retryBaseDelayMs: number
  retryMaxDelayMs: number
  listRefreshIntervalMs: number
  allowedMime: ReadonlySet<string>
}

const positive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0

export function resolveUploadLimits(config?: UploadConfigLike | null): UploadLimits {
  const d = DEFAULT_UPLOAD_LIMITS
  const requested = positive(config?.client_concurrency) ? Math.floor(config!.client_concurrency!) : d.concurrency
  let concurrency = Math.min(d.maxConcurrency, Math.max(d.minConcurrency, requested))
  // Never exceed what the server advertises as its own cap.
  if (positive(config?.max_concurrency)) concurrency = Math.min(concurrency, Math.max(1, Math.floor(config!.max_concurrency!)))
  const mimes = (config?.allowed_mime?.length ? config.allowed_mime : [...d.allowedMime]).map((m) => m.toLowerCase())
  return {
    maxFiles: positive(config?.max_batch_files) ? Math.floor(config!.max_batch_files!) : d.maxFiles,
    maxTotalBytes: positive(config?.max_batch_total_mb) ? config!.max_batch_total_mb! * MB : d.maxTotalBytes,
    maxFileBytes: positive(config?.max_size_mb) ? config!.max_size_mb! * MB : d.maxFileBytes,
    concurrency,
    retryMaxAttempts: d.retryMaxAttempts,
    retryBaseDelayMs: d.retryBaseDelayMs,
    retryMaxDelayMs: d.retryMaxDelayMs,
    listRefreshIntervalMs: d.listRefreshIntervalMs,
    allowedMime: new Set(mimes),
  }
}
